import { useState } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridOverlay,
  GridPagination,
} from "@mui/x-data-grid";
import { CircularProgress, Button, IconButton, Stack, Tooltip } from "@mui/material";
import { Add as IconAdd, Visibility as IconVer, VisibilityOff as IconOcultar } from "@mui/icons-material";
import moment from "moment";
import { formatearCodigoEstado } from "../../helpers/estadoHelper";

/**
 * Componente DataGrid reutilizable con:
 * - Toolbar superior: botón "Nuevo" y paginación arriba.
 * - No tiene exportar a CSV: por seguridad de extracción de datos, de las
 *   tablas no se puede llevar el listado entero.
 * - Overlay de carga (spinner)
 * - Estilos unificados para headers, filas, y espaciado.
 * - Muestra los primeros 50 registros ordenados por FechaRegistro descendente
 *   cuando el listado trae ese campo.
 * - Las cuatro columnas de auditoria (Fecha Reg., Usuario Reg., Fecha Mod. y
 *   Usuario Mod.) se pueden mostrar con el boton del ojo de la barra, pero
 *   por defecto estan ocultas.
 */

// Las columnas de auditoria de la fila: cuando se creo y quien la toco por
// ultima vez. Vienen en casi todas las tablas.
const COLUMNAS_AUDITORIA = [
  {
    field: "fechaRegistro",
    headerName: "Fecha Reg.",
    width: 130,
    sortable: false,
    headerAlign: "left",
    valueFormatter: (valor) => (valor ? moment(valor).format("DD/MM/YYYY HH:mm") : ""),
  },
  {
    field: "usuarioRegistro",
    headerName: "Usuario Reg.",
    width: 120,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "fechaModificacion",
    headerName: "Ultima Fec.Mod.",
    width: 150,
    sortable: false,
    headerAlign: "left",
    valueFormatter: (valor) => (valor ? moment(valor).format("DD/MM/YYYY HH:mm") : ""),
  },
  {
    field: "usuarioModificacion",
    headerName: "Ultimo Usu.Mod.",
    width: 140,
    sortable: false,
    headerAlign: "center",
    align: "center",
  },
];

// 🔹 Toolbar declarada fuera de DataGridBase para preservar una referencia estable
// y evitar que los inputs pierdan el foco al escribir.
const CustomToolbar = ({
  toolbarLeft = null,
  toolbarButtons = [],
  disabled = false,
  hideAuditToggle = false,
  verAuditoria = false,
  setVerAuditoria = () => {},
  toolbarRight = null,
  paginationTop = true,
}) => (
  <GridToolbarContainer
    sx={{
      justifyContent: "space-between",
      alignItems: "center",
      paddingX: 0.5,
      paddingY: 0.25,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      {toolbarLeft}
      {toolbarButtons.map((button, index) =>
        !button.text ? (
          <Tooltip key={index} title={button.titulo || ""}>
            <span>
              <IconButton
                size="small"
                onClick={button.onClick}
                disabled={disabled || button.disabled}
              >
                {button.icon}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            key={index}
            size="small"
            startIcon={button.icon}
            onClick={button.onClick}
            disabled={disabled || button.disabled}
          >
            {button.text}
          </Button>
        )
      )}
      {!hideAuditToggle && (
        <Tooltip
          title={
            verAuditoria
              ? "Ocultar ID y columnas de auditoría"
              : "Mostrar ID y columnas de auditoría"
          }
        >
          <span>
            <IconButton
              size="small"
              aria-label="Columnas de auditoria"
              onClick={() => setVerAuditoria(!verAuditoria)}
              disabled={disabled}
            >
              {verAuditoria ? <IconOcultar /> : <IconVer />}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>

    <Stack direction="row" spacing={1} alignItems="center">
      {toolbarRight}
      {paginationTop && <GridPagination />}
    </Stack>
  </GridToolbarContainer>
);

// 🔹 Capa de carga
const CustomLoadingOverlay = () => (
  <GridOverlay>
    <CircularProgress size={30} />
  </GridOverlay>
);

const DataGridBase = ({
  rows = [],
  columns = [],
  loading = false,
  toolbarButtons = [],
  pageSize = 50,
  pageSizeOptions = [10, 20, 50, 100],
  sx = {},
  paginationTop = true,
  disabled = false,
  showToolbar: forzarToolbar,
  hideAuditToggle = false,
  toolbarLeft = null,
  toolbarRight = null,
  campoOrden = "fechaRegistro",
  ...props
}) => {
  const [verAuditoria, setVerAuditoria] = useState(false);

  // La barra se muestra si hay botones o elementos custom, salvo que se pida lo contrario.
  const showToolbar =
    forzarToolbar != null
      ? forzarToolbar
      : toolbarButtons.length > 0 || Boolean(toolbarLeft) || Boolean(toolbarRight);

  // Todos los listados se muestran ordenados por la fecha de registro, de mas
  // reciente a mas antiguo. Para poder ordenar se agrega la columna si el
  // listado no la declara, y queda oculta.
  const declaradas = (campo) =>
    columns.some((column) => column.field === campo);

  const pareceFecha = (column) => {
    if (column.valueFormatter || column.renderCell || column.valueGetter) return false;
    if (column.type === "date" || column.type === "dateTime") return true;
    return /fecha/i.test(String(column.field || ""));
  };

  const pareceEstado = (column) => {
    return (
      column.field === "estado" ||
      column.field === "idEstado" ||
      String(column.headerName || "").trim().toLowerCase() === "estado"
    );
  };

  const formatearFechaListado = (valor) => {
    if (valor == null || valor === "") return "";
    const texto = String(valor);
    const fecha = moment(texto);
    if (!fecha.isValid()) return texto;
    const tieneHora = /[T ]\d{2}:\d{2}/.test(texto) && !/T00:00:00/.test(texto);
    return fecha.format(tieneHora ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
  };

  const columnasNormalizadas = columns.map((column) => {
    if (pareceFecha(column)) {
      return { ...column, valueFormatter: (valor) => formatearFechaListado(valor) };
    }
    if (pareceEstado(column)) {
      return {
        ...column,
        headerAlign: "center",
        align: "center",
        renderCell: (p) => formatearCodigoEstado(p.value, p.row),
      };
    }
    return column;
  });

  const agregadasAuditoria = COLUMNAS_AUDITORIA.filter(
    (column) => !declaradas(column.field)
  );

  const columnas = [
    ...columnasNormalizadas,
    ...agregadasAuditoria,
    ...(declaradas(campoOrden) || agregadasAuditoria.some((c) => c.field === campoOrden)
      ? []
      : [{ field: campoOrden, headerName: "Fecha Registro" }]),
  ];

  // Columnas que son identificadores y se muestran solo cuando verAuditoria esta activo (boton ojo)
  const esColumnaIdOcultable = (column) =>
    column.ocultarConAuditoria !== false && (
      column.esId ||
      column.field === "id" ||
      column.field === "idTerapia" ||
      column.field === "idTerapiaPlan" ||
      column.field === "idTrabajador" ||
      column.field === "idComprobante" ||
      column.field === "idSesion" ||
      column.headerName === "ID"
    );

  const columnasId = columns.filter(esColumnaIdOcultable);

  const columnVisibilityModel = {
    ...(!declaradas(campoOrden) && !agregadasAuditoria.some((c) => c.field === campoOrden)
      ? { [campoOrden]: false }
      : {}),
    ...agregadasAuditoria.reduce(
      (modelo, columna) => ({ ...modelo, [columna.field]: verAuditoria }),
      {}
    ),
    ...columnasId.reduce(
      (modelo, columna) => ({ ...modelo, [columna.field]: verAuditoria }),
      {}
    ),
    ...(props.columnVisibilityModel || {}),
  };

  const estadoInicial = {
    pagination: {
      paginationModel: {
        pageSize,
      },
    },
    sorting: {
      sortModel: [{ field: campoOrden, sort: "desc" }],
    },
  };

  const opcionesBase = props.rowsPerPageOptions || pageSizeOptions || [10, 20, 50, 100];
  const opcionesFinales = Array.from(new Set([...opcionesBase, pageSize])).sort((a, b) => a - b);

  return (
    <DataGrid
      rows={rows}
      columns={columnas}
      loading={loading}
      autoHeight
      disableColumnMenu
      rowHeight={25}
      columnHeaderHeight={32}
      pageSizeOptions={opcionesFinales}
      initialState={estadoInicial}
      columnVisibilityModel={columnVisibilityModel}
      slots={{
        ...(showToolbar && { toolbar: CustomToolbar }),
        loadingOverlay: CustomLoadingOverlay,
      }}
      slotProps={{
        toolbar: {
          toolbarLeft,
          toolbarButtons,
          disabled,
          hideAuditToggle,
          verAuditoria,
          setVerAuditoria,
          toolbarRight,
          paginationTop,
        },
      }}
      localeText={{
        MuiTablePagination: {
          labelRowsPerPage: '',
        },
      }}
      sx={{
        "& .MuiDataGrid-columnHeader": {
          backgroundColor: "#1976d2",
          color: "white",
        },
        "& .MuiDataGrid-cell:hover": {
          color: "primary.main",
        },
        "& .MuiDataGrid-columnSeparator": {
          display: "none",
        },
        "& .MuiDataGrid-footerContainer": {
          display: paginationTop ? "none" : "flex",
        },
        "& .MuiDataGrid-toolbarContainer": {
          minHeight: 28,
          padding: 0,
          gap: 0.5,
        },
        "& .MuiTablePagination-root": {
          minHeight: 28,
          margin: 0,
          padding: 0,
          fontSize: "0.75rem",
        },
        "& .MuiTablePagination-toolbar": {
          minHeight: 28,
          padding: 0,
        },
        "& .MuiTablePagination-displayedRows, & .MuiTablePagination-actions": {
          margin: 0,
          padding: 0,
        },
        overflowX: "auto",
        overflowY: "hidden",
        "& .MuiDataGrid-main": {
          overflowX: "auto",
          overflowY: "hidden",
          paddingBottom: "36px",
        },
        ...sx,
      }}
      {...props}
    />
  );
};

export default DataGridBase;
